import { apiAxios } from "./apiConfig";

/* =====================================================
   LESSON LIST (GROUPED BY WEEK)
===================================================== */

export interface Lesson {
  id: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  lessonType: "video" | "document" | "website";
  attachmentCount: number;
  creator: {
    name: string;
  } | null;
  updater: any;
}

export interface LessonWeek {
  id: string;
  name: string;
  lessons: Lesson[];
}

export interface LessonListResponse {
  status: boolean;
  data: LessonWeek[];
}

export const getLessonList = async (): Promise<LessonListResponse> => {
  const response = await apiAxios.get("/lesson/list");
  return response.data;
};

/* =====================================================
   WEEK LIST
===================================================== */

export interface Week {
  id: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface WeekListResponse {
  status: boolean;
  data: Week[];
}

export const getWeekList = async (): Promise<WeekListResponse> => {
  const response = await apiAxios.get("/week/list");
  return response.data;
};

/* =====================================================
   CREATE LESSON
===================================================== */

export interface LessonTypeDetail {
  lessonDuration?: string;
  lessonUrl?: string;
  lessonFileId?: string;
}

export interface OtherAttachment {
  attachmentType: "video" | "document" | "website";
  attachmentDuration?: string;
  attachmentUrl?: string;
  attachmentFileId?: string;
}

export interface LessonCategory {
  categoryId: string;
  isActive: boolean;
}

export interface CreateLessonPayload {
  weekId: string;
  name: string;
  description: string;
  lessonType: "video" | "document" | "website";
  lessonTypeDetails: LessonTypeDetail[];
  otherAttachments: OtherAttachment[];
  lessonLongDescription: string;
  categories: LessonCategory[];
  buttonName: string;
}

export interface CreateLessonResponse {
  status: boolean;
  data: any;
}

export const createLesson = async (
  payload: CreateLessonPayload,
): Promise<CreateLessonResponse> => {
  const response = await apiAxios.post("/lesson/create", payload);
  return response.data;
};

/* =====================================================
   UPDATE LESSON
===================================================== */

export const updateLesson = async (
  lessonId: string,
  payload: CreateLessonPayload,
): Promise<CreateLessonResponse> => {
  const response = await apiAxios.put(`/lesson/${lessonId}`, payload);
  return response.data;
};

/* =====================================================
   GET LESSON BY ID (DETAILS)
===================================================== */

export interface LessonDetailCategory {
  id: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface LessonDetails {
  weekId: string;
  id: string;
  name: string;
  description: string;
  lessonType: "video" | "document" | "website";
  lessonTypeDetails: LessonTypeDetail[];
  otherAttachments: OtherAttachment[];
  isActive: boolean;
  lessonLongDescription: string;
  categoriesMapped: LessonDetailCategory[];
  buttonName: string;
  lessonFileId: string;
}

export interface LessonDetailsResponse {
  status: boolean;
  data: LessonDetails;
}

export const getLessonById = async (
  lessonId: string,
): Promise<LessonDetailsResponse> => {
  const response = await apiAxios.get(`/lesson/${lessonId}`);
  return response.data;
};

/* =====================================================
   REARRANGE LESSON
===================================================== */

export interface RearrangeLessonPayload {
  lessonId: string;
  fromWeekId: string;
  toWeekId: string;
  oldSortOrder: number;
  newSortOrder: number;
}

export interface RearrangeLessonResponse {
  status: boolean;
  data: string;
}

export const rearrangeLesson = async (
  payload: RearrangeLessonPayload,
): Promise<RearrangeLessonResponse> => {
  const response = await apiAxios.put("/lesson/rearrange", payload);
  return response.data;
};

export interface DeleteLessonResponse {
  status: boolean;
  data: string;
}

export const deleteLesson = async (
  lessonId: string,
): Promise<DeleteLessonResponse> => {
  const response = await apiAxios.delete(`/lesson/${lessonId}`);
  return response.data;
};
